import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Table,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "Store/GenericStore/GenericStore";
import apiGlobal from "global/api.global";
import Loading from "Components/Loading";
import Message from "Components/Message";
import * as Yup from "yup";
import ErrorTooltip from "Components/ErrorTooltip";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { dateTimeFormat } from "GenericForms/Helper";
import VesselDetailsHeader from "Components/VesselDetailsHeader";
import { errorToast } from "Components/Toasts";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Logo from '../Media/LogoLogin.png';
interface FWROBDataType {
    activeTab: string
}

const FWROBData = ({ activeTab }: FWROBDataType) => {
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    const record = Vessels.find((rec: any) => rec.id === VesselID);
    const vesselName = record?.vessel_name;
    const [fwData, setFwData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vessel: VesselID,
            startDate: "",
            endDate: "",
        },
        validationSchema: Yup.object().shape({
            startDate: Yup.date().required(commonValidationMessages.required),
            endDate: Yup.date().required(commonValidationMessages.required),
        }),
        onSubmit: (values) => {
            setLoading(true);
            setError("");
            formik.setSubmitting(true);
            apiGlobal
                .post(
                    "rob/report_fresh_water_rob/get_fresh_water_rob_between_date_range/",
                    values
                )
                .then((res) => {
                    setFwData(res.data || []); // ensure always an array
                })
                .catch((error: any) => {
                    setError("");
                    setError(error.response.data.message || "Something went wrong");
                    errorToast(error.response.data.message);
                    setFwData([]);
                })
                .finally(() => {
                    setLoading(false);
                    formik.setSubmitting(false);
                });
        },
    });

    const downloadExcel = async () => {
        if (!fwData || fwData.length === 0) {
            errorToast("No data found!");
            return;
        }

        const { startDate, endDate } = formik.values;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Fresh Water ROB");

        // -----------------------------------------------------
        //  MAIN HEADING (NO LOGO)
        // -----------------------------------------------------
        const mainHeading = `Fresh Water ROB Data from ${startDate} to ${endDate} for ${vesselName}`;
        const mainHeaderRow = sheet.addRow([mainHeading]);

        sheet.mergeCells(mainHeaderRow.number, 1, mainHeaderRow.number, 7);

        mainHeaderRow.eachCell((cell) => {
            cell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "4472C4" },
            };
        });

        sheet.addRow([]);

        // -----------------------------------------------------
        //  TABLE HEADERS
        // -----------------------------------------------------
        const headers = [
            "#",
            "Report Name (Time)",
            "Produced",
            "Received",
            "Domestic Consumed",
            "Distilled Consumed",
            "ROB"
        ];

        const headerRow = sheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "D9E1F2" },
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // -----------------------------------------------------
        //  TABLE DATA
        // -----------------------------------------------------
        fwData.forEach((item, index) => {
            const row = sheet.addRow([
                index + 1,
                `${item.report_name} (${dateTimeFormat(item.report_time)})`,
                item.amount_of_fresh_water_produced ?? "-",
                item.amount_of_fresh_water_received ?? "-",
                item.amount_of_domestic_water_consumed ?? "-",
                item.amount_of_distilled_water_consumed ?? "-",
                item.rob ?? "-",
            ]);

            row.eachCell((cell, colNum) => {
                cell.alignment = {
                    horizontal: colNum <= 2 ? "left" : "center",
                    vertical: "middle"
                };

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };

                if (colNum === 7) cell.font = { bold: true };

                if (index % 2 === 0) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "F2F2F2" },
                    };
                }
            });
        });

        // -----------------------------------------------------
        //  AUTO COLUMN WIDTHS
        // -----------------------------------------------------
        sheet.columns.forEach((col, i) => {
            if (i === 0) col.width = 8;
            else if (i === 1) col.width = 40;
            else col.width = 18;
        });

        // -----------------------------------------------------
        //  FOOTER (TWO EMPTY ROWS + LOGO + TEXT)
        // -----------------------------------------------------
        sheet.addRow([]);
        sheet.addRow([]);

        // ---- Convert logo to base64 ----
        const logoResponse = await fetch(Logo);
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
        });

        const logoId = workbook.addImage({
            base64: (logoBase64 as string).split(",")[1],
            extension: "png",
        });

        // ---- Add footer row ----
        const footerRow = sheet.addRow(["", "Created by ecoSAIL Infotech"]);

        // Merge footer text across cells (columns 2 to 7)
        sheet.mergeCells(footerRow.number, 2, footerRow.number, 2);

        footerRow.getCell(2).alignment = {
            horizontal: "center",
            vertical: "middle",
        };
        footerRow.getCell(2).font = {
            italic: true,
            size: 11,
            color: { argb: "666666" },
        };

        // ---- Add logo on the left (column 1) ----
        sheet.addImage(logoId, {
            tl: { col: 0, row: footerRow.number - 1 },
            ext: { width: 20, height: 20 },
        });

        // -----------------------------------------------------
        //  DOWNLOAD FILE
        // -----------------------------------------------------
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `fresh_water_rob_${startDate}_${endDate}.xlsx`);
    };
    /**delete data when vessel changes */
    useEffect(() => {
        setFwData([]);
    }, [VesselID]);
    return (
        <Card>
            <CardBody>
                <Card>
                    <Row className="mb-2 p-2">
                        <Col sm={3}>
                            <h4 className="page_title pos-start mt-2">Fresh Water ROB Data</h4>
                        </Col>
                        <Col sm={9}>
                            <VesselDetailsHeader />
                        </Col>
                    </Row>
                </Card>
                {/* DATE FILTER FORM */}
                <Form onSubmit={formik.handleSubmit}>
                    <Row className="mb-3">
                        <Col md="3">
                            <FormGroup className="mb-0">
                                <Label className="mb-1 asteric">Start Date</Label>
                                <Input
                                    type="datetime-local"
                                    name="startDate"
                                    id="startDate"
                                    className="datetimepicker text-uppercase"
                                    onFocus={(e) => e.target.select()}
                                    value={formik.values.startDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    max={"9999-12-31T00:00"}
                                />
                            </FormGroup>
                            {formik.touched.startDate && formik.errors.startDate && activeTab === '3' && (
                                <ErrorTooltip
                                    message={formik.errors.startDate}
                                    target={"startDate"}
                                    open={true}
                                />
                            )}
                        </Col>

                        {/* End Date */}
                        <Col md="3">
                            <FormGroup className="mb-0">
                                <Label className="mb-1 asteric">End Date</Label>
                                <Input
                                    type="datetime-local"
                                    name="endDate"
                                    id='endDate'
                                    className="datetimepicker text-uppercase"
                                    onFocus={(e) => e.target.select()}
                                    value={formik.values.endDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    max={"9999-12-31T00:00"}
                                />
                            </FormGroup>
                            {formik.touched.endDate && formik.errors.endDate && activeTab === '3' && (
                                <ErrorTooltip
                                    message={formik.errors.endDate}
                                    target={"endDate"}
                                    open={true}
                                />
                            )}
                        </Col>

                        <Col md="2" className="d-flex align-items-end">
                            <Button
                                type="submit"
                                color="primary"
                                size="md"
                                className="mb-3"
                                disabled={formik.isSubmitting || !formik.isValid}
                            >
                                Generate
                            </Button>
                        </Col>
                    </Row>
                    <FormValuesDebug values={[formik.values, formik.errors, formik.touched]} />
                </Form>
                {loading && <Loading message="Loading Data!" />}
                {error && <Message message={error} type="danger" />}
                {!loading && !error && fwData?.length > 0 &&
                    <Table bordered className="p-0 m-0">
                        <thead className="table-light sticky-header">
                            <tr>
                                <th>#</th>
                                <th>Report Name (Time)</th>
                                <th>Produced</th>
                                <th>Received</th>
                                <th>Domestic Consumed</th>
                                <th>Distilled Consumption</th>
                                <th>ROB</th>
                            </tr>
                        </thead>

                        <tbody>
                            {(fwData?.length > 0) && (
                                fwData?.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{index + 1}</td>
                                        <td>{item.report_name} ( {dateTimeFormat(item.report_time)} )</td>
                                        <td>{item.amount_of_fresh_water_produced ?? "-"}</td>
                                        <td>{item.amount_of_fresh_water_received ?? "-"}</td>
                                        <td>{item.amount_of_domestic_water_consumed ?? "-"}</td>
                                        <td>{item.amount_of_distilled_water_consumed ?? "-"}</td>
                                        <td><b>{item.rob ?? "-"}</b></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                }
                {/* DOWNLOAD BUTTON ALIGNED TO END */}
                {fwData?.length > 0 && (
                    <Row className="mt-4 justify-content-end">
                        <Col md="2" className="text-end">
                            <div className="justify_right">
                                <button
                                    type="button"
                                    id="download"
                                    className="btn btn-primary waves-effect btn-label waves-light mb-2"
                                    onClick={downloadExcel}
                                >
                                    <i className="mdi mdi-download label-icon" />
                                    Download
                                </button>
                            </div>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default FWROBData;